import { useEffect, useRef, useState } from "react";
import PageLayout from "../components/PageLayout";
import PinIcon from "../components/PinIcon";
import { useLens } from "../lens/LensProvider";
import { fetchPilotsPage } from "../services/pilotsService";
import { PilotRef } from "../types/pilots";

// The API serves at most 200 pilots a request, so the directory pages through until it has them all.
const PAGE_LIMIT = 200;
const MAX_PAGES = 50;
const ROWS_PER_REVEAL = 100;

type SortKey = "name" | "last_seen_at" | "proficiency_count" | "achievement_count";
type SortDir = "asc" | "desc";

function encodeHashSegment(value: string): string {
  return encodeURIComponent(value);
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 2) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function sortPilots(pilots: PilotRef[], key: SortKey, dir: SortDir): PilotRef[] {
  return [...pilots].sort((a, b) => {
    let cmp = 0;
    if (key === "name") {
      cmp = (a.name ?? "").localeCompare(b.name ?? "");
    } else if (key === "last_seen_at") {
      const ta = a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0;
      const tb = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0;
      cmp = ta - tb;
    } else if (key === "proficiency_count") {
      const proficiencyCountA = (a.basic_count ?? 0) + (a.advanced_count ?? 0) + (a.expert_count ?? 0) + (a.master_count ?? 0);
      const proficiencyCountB = (b.basic_count ?? 0) + (b.advanced_count ?? 0) + (b.expert_count ?? 0) + (b.master_count ?? 0);
      cmp = proficiencyCountA - proficiencyCountB;
      if (cmp === 0) {
        cmp = (a.name ?? "").localeCompare(b.name ?? "");
      }
    } else {
      cmp = (a[key] ?? 0) - (b[key] ?? 0);
    }
    return dir === "asc" ? cmp : -cmp;
  });
}

const PilotsPage = () => {
  const { pinnedUid } = useLens();
  const [query, setQuery] = useState("");
  const [pilots, setPilots] = useState<PilotRef[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [visibleCount, setVisibleCount] = useState(ROWS_PER_REVEAL);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey | null>("last_seen_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const current = () => requestId === requestIdRef.current;
    setLoading(true);
    setLoadingMore(false);
    setError(null);
    setPilots([]);
    setTotal(null);
    setVisibleCount(ROWS_PER_REVEAL);
    setSortKey("last_seen_at");
    setSortDir("desc");

    const timer = window.setTimeout(async () => {
      const seen = new Set<string>();
      let loaded: PilotRef[] = [];
      try {
        for (let page = 0; page < MAX_PAGES; page += 1) {
          const result = await fetchPilotsPage(query, { limit: PAGE_LIMIT, offset: loaded.length });
          if (!current()) return;
          const fresh = result.pilots.filter((pilot) => !seen.has(pilot.uid));
          fresh.forEach((pilot) => seen.add(pilot.uid));
          loaded = loaded.concat(fresh);
          setPilots(loaded);
          setTotal(result.total);
          setLoading(false);

          // A short page, a page of repeats (an API without offset) or the full count ends the walk.
          const done = result.pilots.length < PAGE_LIMIT || fresh.length === 0 || (result.total !== null && loaded.length >= result.total);
          if (done) break;
          setLoadingMore(true);
        }
      } catch (err) {
        if (!current()) return;
        setError(err instanceof Error ? err.message : "Failed to load pilots");
        setLoading(false);
      } finally {
        if (current()) setLoadingMore(false);
      }
    }, 180);

    return () => window.clearTimeout(timer);
  }, [query, refreshTick]);

  const handleClear = () => setQuery("");
  const handleRetry = () => setRefreshTick((v) => v + 1);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  const sorted = sortKey ? sortPilots(pilots, sortKey, sortDir) : pilots;
  const displayed = sorted.slice(0, visibleCount);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || visibleCount >= sorted.length) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) setVisibleCount((count) => count + ROWS_PER_REVEAL);
    }, { rootMargin: "400px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [visibleCount, sorted.length]);

  const indicator = (key: SortKey) =>
    sortKey === key ? (sortDir === "desc" ? " ↓" : " ↑") : "";

  return (
    <PageLayout route="pilots" hero={{
      photo: "pilots", focus: "50% 29%", title: "Pilots",
      body: "Find a pilot to see their stats, proficiencies and achievements.",
    }}>
      <section className="panel panel-tight">
        <div className="pilots-header">
          <div className="pilots-header-left">
            <label className="filter-label" htmlFor="pilot_query">Pilot search</label>
            <div className="pilots-search-box">
              <span className="facet-input-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M16.5 16.5 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
              <input
                id="pilot_query"
                className="pilots-search-field"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search pilots…"
                autoComplete="off"
                spellCheck={false}
                aria-label="Search pilots"
              />
              {query && (
                <button type="button" className="pilots-search-clear" onClick={handleClear} aria-label="Clear search">
                  ×
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="panel panel-list">
        {error && (
          <div className="alert">
            <span>{error}</span>
            <button type="button" onClick={handleRetry}>Retry</button>
          </div>
        )}

        {loading && <div className="status-area"><span className="status">Loading pilots…</span></div>}

        {!loading && pilots.length === 0 && !error && (
          <div className="empty-state">No pilots found. Try a different search.</div>
        )}

        {!loading && pilots.length > 0 && (
          <div className="pilot-table" role="table" aria-label="Pilots">
            <div className="pilot-table-head" role="rowgroup">
              <div className="pilot-table-row" role="row">
                <button className={"pilot-table-cell pilot-table-header pilot-table-sort-btn" + (sortKey === "name" ? " is-sorted" : "")} role="columnheader" onClick={() => handleSort("name")}>
                  Pilot{indicator("name")}
                </button>
                <button className={"pilot-table-cell pilot-table-header pilot-table-sort-btn" + (sortKey === "last_seen_at" ? " is-sorted" : "")} role="columnheader" onClick={() => handleSort("last_seen_at")}>
                  Last seen{indicator("last_seen_at")}
                </button>
                <button
                  className={"pilot-table-cell pilot-table-header pilot-table-header-stat pilot-table-header-proficiencies pilot-table-sort-btn" + (sortKey === "proficiency_count" ? " is-sorted" : "")}
                  role="columnheader"
                  onClick={() => handleSort("proficiency_count")}
                >
                  <span>Proficiencies{indicator("proficiency_count")}</span>
                </button>
                <button className={"pilot-table-cell pilot-table-header pilot-table-header-stat pilot-table-header-achievements pilot-table-sort-btn" + (sortKey === "achievement_count" ? " is-sorted" : "")} role="columnheader" onClick={() => handleSort("achievement_count")}>
                  <span>Achievements{sortKey === "achievement_count" ? (sortDir === "desc" ? " ←" : " →") : ""}</span>
                </button>
              </div>
            </div>
            <div className="pilot-table-body" role="rowgroup">
              {displayed.map((pilot) => {
                const proficiencyCount = (pilot.basic_count ?? 0) + (pilot.advanced_count ?? 0) + (pilot.expert_count ?? 0) + (pilot.master_count ?? 0);
                return (
                  <a
                    key={pilot.uid}
                    className={"pilot-table-row pilot-table-row-link" + (pilot.uid === pinnedUid ? " is-pinned" : "")}
                    role="row"
                    href={`#/pilots/${encodeHashSegment(pilot.uid)}`}
                    aria-label={`Go to ${pilot.name ?? "unknown"} profile`}
                  >
                    <span className="pilot-table-cell pilot-table-cell-name" role="cell">
                      {pilot.name ?? "Unknown pilot"}
                      {pilot.uid === pinnedUid && <span className="pilot-table-pin" role="img" aria-label="Pinned pilot" title="Pinned pilot"><PinIcon size={13} /></span>}
                    </span>
                    <span className="pilot-table-cell pilot-table-cell-muted" role="cell">
                      {relativeTime(pilot.last_seen_at)}
                    </span>
                    <span className={"pilot-table-cell pilot-table-cell-stat" + (proficiencyCount === 0 ? " is-zero" : "")} role="cell">
                      {proficiencyCount}
                    </span>
                    <span className={"pilot-table-cell pilot-table-cell-stat" + ((pilot.achievement_count ?? 0) === 0 ? " is-zero" : "")} role="cell">
                      {pilot.achievement_count ?? 0}
                    </span>
                  </a>
                );
              })}
            </div>
            <div ref={sentinelRef} className="sentinel" aria-hidden="true" />
          </div>
        )}

        {!loading && pilots.length > 0 && (
          <div className="status-area">
            <span className="status">
              {loadingMore
                ? `Loading more pilots… ${pilots.length}${total !== null ? ` of ${total}` : ""}`
                : `${total ?? pilots.length} ${(total ?? pilots.length) === 1 ? "pilot" : "pilots"}`}
            </span>
          </div>
        )}
      </section>
    </PageLayout>
  );
};

export default PilotsPage;

